import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Users, ShieldAlert, Database, BarChart3, RefreshCw, ArrowLeft,
  Loader2, Trash2, ShieldCheck, ShieldOff, UserX, UserCheck, Shield, FolderTree,
  Activity, Undo2, AlertCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import AdminAddTorrent from "@/components/AdminAddTorrent";
import AdminSmilies from "@/components/AdminSmilies";
import AdminForums from "@/components/AdminForums";
import AdminSources from "@/components/AdminSources";
import AdminPartners from "@/components/AdminPartners";
import AdminReports from "@/components/AdminReports";
import AdminAnalytics from "@/components/AdminAnalytics";
import AdminDigest from "@/components/AdminDigest";
import AdminLibrary from "@/components/AdminLibrary";

function Stat({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5 relative overflow-hidden">
      <div className="absolute -right-4 -top-4 w-20 h-20 rounded-full" style={{ background: `${color}22` }} />
      <div className="flex items-center gap-3 relative">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}33`, color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">{label}</div>
          <div className="font-display text-2xl font-bold">{value ?? "—"}</div>
        </div>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [cache, setCache] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditFilter, setAuditFilter] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [syncing, setSyncing] = useState(false);
  const isAdmin = user?.role === "admin";
  const isMod = user?.role === "mod" || isAdmin;

  const reload = async () => {
    const calls = [];
    if (isAdmin) {
      calls.push(api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}));
      calls.push(api.get("/admin/users", { params: { q: q || undefined } }).then((r) => setUsers(r.data)).catch(() => {}));
      calls.push(api.get("/admin/torrents-cache").then((r) => setCache(r.data)).catch(() => {}));
    }
    calls.push(api.get("/admin/torrents/blocked").then((r) => setBlocked(r.data)).catch(() => {}));
    calls.push(api.get("/admin/torrents/classifications").then((r) => setOverrides(r.data)).catch(() => {}));
    calls.push(api.get("/admin/audit", { params: { action: auditFilter || undefined, limit: 100 } })
      .then((r) => setAudit(r.data)).catch(() => {}));
    await Promise.all(calls);
  };

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "mod")) { navigate("/"); return; }
    reload().catch(() => toast.error("Failed to load admin data"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading, auditFilter]);

  if (loading || !user) {
    return <div className="min-h-screen bg-black flex items-center justify-center text-[var(--tt-text-muted)]"><Loader2 className="w-5 h-5 animate-spin"/></div>;
  }

  const action = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sync");
      toast.success(`Warmed ${data.warmed} entries`);
      await reload();
    } catch {
      toast.error("Sync failed");
    } finally { setSyncing(false); }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <header className="sticky top-0 z-30 bg-black/80 backdrop-blur-xl border-b border-[var(--tt-border)] px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="text-[var(--tt-text-muted)] hover:text-white flex items-center gap-2" data-testid="admin-back-home">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
          <h1 className="font-display text-2xl font-bold">{isAdmin ? "Admin Panel" : "Moderator Panel"}</h1>
          <span className="text-xs bg-[var(--tt-yellow)]/20 text-[var(--tt-yellow)] px-2 py-0.5 rounded font-semibold uppercase tracking-wider">{user.email}</span>
        </div>
        {isAdmin && (
          <button
            onClick={sync}
            disabled={syncing}
            data-testid="admin-sync-btn"
            className="bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-semibold px-4 py-2 rounded-full flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />
            Sync Torrents Cache
          </button>
        )}
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {isAdmin && (
          <>
        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="admin-stats">
          <Stat label="Users" value={stats?.users_total} icon={Users} color="#FF2A6D" />
          <Stat label="Admins" value={stats?.admins} icon={ShieldCheck} color="#F9A826" />
          <Stat label="Google" value={stats?.google_users} icon={ShieldCheck} color="#9D4CDD" />
          <Stat label="Banned" value={stats?.users_banned} icon={ShieldAlert} color="#ef4444" />
          <Stat label="Sessions" value={stats?.active_sessions} icon={BarChart3} color="#22c55e" />
          <Stat label="Cached" value={stats?.cached_searches} icon={Database} color="#1c89df" />
        </section>

        {/* Users */}
        <section className="mt-10">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-display text-xl font-bold">Users</h2>
            <input
              value={q} onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && reload()}
              data-testid="admin-users-search"
              placeholder="Search email..."
              className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full px-4 py-1.5 text-xs outline-none focus:border-[var(--tt-primary)] flex-1 max-w-sm"
            />
          </div>
          <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-users-table">
                <thead className="bg-black/40 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">
                  <tr>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Role</th>
                    <th className="text-left px-4 py-3">Tier</th>
                    <th className="text-left px-4 py-3">Provider</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-left px-4 py-3">Created</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-[var(--tt-border)] hover:bg-white/[0.03]" data-testid={`admin-user-row-${u.user_id}`}>
                      <td className="px-4 py-3 font-mono-tt text-xs">{u.email}</td>
                      <td className="px-4 py-3">{u.name || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${u.role === "admin" ? "bg-[var(--tt-yellow)] text-black" : "bg-zinc-700 text-zinc-200"}`}>{u.role}</span>
                      </td>
                      <td className="px-4 py-3">
                        <TierControl
                          user={u}
                          busy={busy}
                          onGrant={(tier, months) =>
                            action(
                              () => api.post(`/admin/users/${u.user_id}/tier`, { tier, months }),
                              tier === "none" ? "Tier cleared" : `Granted ${tier}`,
                            )
                          }
                        />
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--tt-text-muted)]">{u.auth_provider}</td>
                      <td className="px-4 py-3">
                        {u.is_banned
                          ? <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-red-500/20 text-red-400">Banned</span>
                          : <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-400">Active</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-[var(--tt-text-muted)] font-mono-tt">{u.created_at?.slice(0, 10)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {u.role !== "admin" && (
                            u.is_banned ? (
                              <button disabled={busy} onClick={() => action(() => api.post(`/admin/users/${u.user_id}/unban`), "Unbanned")}
                                data-testid={`admin-user-unban-${u.user_id}`}
                                className="p-1.5 rounded hover:bg-white/10 text-emerald-400" title="Unban">
                                <UserCheck className="w-4 h-4" />
                              </button>
                            ) : (
                              <button disabled={busy} onClick={() => action(() => api.post(`/admin/users/${u.user_id}/ban`), "Banned")}
                                data-testid={`admin-user-ban-${u.user_id}`}
                                className="p-1.5 rounded hover:bg-white/10 text-red-400" title="Ban">
                                <UserX className="w-4 h-4" />
                              </button>
                            )
                          )}
                          {u.role === "admin" ? (
                            u.user_id !== user.user_id && (
                              <button disabled={busy} onClick={() => action(() => api.post(`/admin/users/${u.user_id}/demote`), "Demoted")}
                                data-testid={`admin-user-demote-${u.user_id}`}
                                className="p-1.5 rounded hover:bg-white/10 text-zinc-300" title="Demote">
                                <ShieldOff className="w-4 h-4" />
                              </button>
                            )
                          ) : (
                            <button disabled={busy} onClick={() => action(() => api.post(`/admin/users/${u.user_id}/promote`), "Promoted to admin")}
                              data-testid={`admin-user-promote-${u.user_id}`}
                              className="p-1.5 rounded hover:bg-white/10 text-[var(--tt-yellow)]" title="Promote">
                              <ShieldCheck className="w-4 h-4" />
                            </button>
                          )}
                          {isAdmin && u.role !== "admin" && u.role !== "mod" && (
                            <button disabled={busy} onClick={() => action(() => api.post(`/admin/users/${u.user_id}/promote-mod`), "Promoted to mod")}
                              data-testid={`admin-user-promote-mod-${u.user_id}`}
                              className="p-1.5 rounded hover:bg-white/10 text-emerald-400" title="Promote to mod">
                              <Shield className="w-4 h-4" />
                            </button>
                          )}
                          {isAdmin && u.role === "mod" && (
                            <button disabled={busy} onClick={() => action(() => api.post(`/admin/users/${u.user_id}/demote`), "Demoted to user")}
                              data-testid={`admin-user-demote-mod-${u.user_id}`}
                              className="p-1.5 rounded hover:bg-white/10 text-zinc-300" title="Remove mod">
                              <ShieldOff className="w-4 h-4" />
                            </button>
                          )}
                          {u.role !== "admin" && (
                            <button disabled={busy} onClick={() => action(() => api.delete(`/admin/users/${u.user_id}`), "Deleted")}
                              data-testid={`admin-user-delete-${u.user_id}`}
                              className="p-1.5 rounded hover:bg-white/10 text-red-500" title="Delete">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                  {users.length === 0 && (
                    <tr><td colSpan={8} className="py-10 text-center text-[var(--tt-text-muted)]">No users yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        </>
        )}

        {/* Moderation */}
        <section className="mt-10" data-testid="admin-moderation-panel">          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Shield className="w-5 h-5 text-[var(--tt-primary)]" />
              Moderation
            </h2>
            <span className="text-[10px] uppercase tracking-wider bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] px-2 py-0.5 rounded font-semibold">
              {isAdmin ? "Admin" : "Moderator"}
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--tt-border)] flex items-center gap-2">
                <h3 className="text-sm font-bold">Blocked Torrents</h3>
                <span className="text-[10px] font-mono-tt bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] px-2 py-0.5 rounded">{blocked.length}</span>
              </div>
              <ul className="max-h-72 overflow-y-auto divide-y divide-[var(--tt-border)]" data-testid="admin-blocked-list">
                {blocked.length === 0 && <li className="text-xs text-[var(--tt-text-muted)] p-4">No torrents blocked.</li>}
                {blocked.map((b) => (
                  <li key={b.info_hash} className="p-3 flex items-start gap-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-tt text-[10px] text-[var(--tt-text-muted)] truncate">{b.info_hash}</div>
                      {b.name && <div className="truncate">{b.name}</div>}
                      {b.reason && <div className="text-[var(--tt-text-muted)] italic mt-1">&ldquo;{b.reason}&rdquo;</div>}
                      <div className="text-[10px] text-[var(--tt-text-faint)] mt-1">by {b.blocked_by_name}</div>
                    </div>
                    <button disabled={busy}
                      onClick={() => action(() => api.delete(`/admin/torrents/block/${b.info_hash}`), "Unblocked")}
                      data-testid={`admin-unblock-${b.info_hash}`}
                      className="text-[10px] font-bold px-2 py-1 rounded bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25">
                      UNBLOCK
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
              <div className="px-4 py-3 border-b border-[var(--tt-border)] flex items-center gap-2">
                <FolderTree className="w-4 h-4 text-[var(--tt-purple)]" />
                <h3 className="text-sm font-bold">Reclassifications</h3>
                <span className="text-[10px] font-mono-tt bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] px-2 py-0.5 rounded">{overrides.length}</span>
              </div>
              <ul className="max-h-72 overflow-y-auto divide-y divide-[var(--tt-border)]" data-testid="admin-overrides-list">
                {overrides.length === 0 && <li className="text-xs text-[var(--tt-text-muted)] p-4">No reclassifications yet. Mods can move TV torrents into the right section from the detail modal.</li>}
                {overrides.map((o) => (
                  <li key={o.info_hash} className="p-3 flex items-start gap-2 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-mono-tt text-[10px] text-[var(--tt-text-muted)] truncate">{o.info_hash}</div>
                      {o.name && <div className="truncate">{o.name}</div>}
                      <div className="text-[10px] mt-1">
                        Category: <span className="font-bold uppercase text-[var(--tt-purple)]">{o.category}</span>
                      </div>
                      <div className="text-[10px] text-[var(--tt-text-faint)] mt-1">by {o.updated_by_name}</div>
                    </div>
                    <button disabled={busy}
                      onClick={() => action(() => api.delete(`/admin/torrents/classify/${o.info_hash}`), "Cleared")}
                      data-testid={`admin-unclassify-${o.info_hash}`}
                      className="text-[10px] font-bold px-2 py-1 rounded bg-zinc-700 text-zinc-200 hover:bg-zinc-600">
                      RESET
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Add Torrent — available to mods + admins */}
        <AdminAddTorrent onAdded={reload} />

        {/* Torrent Reports — available to mods + admins */}
        <AdminReports />

        {/* Chat smilies — admin only */}
        {isAdmin && <AdminSmilies />}

        {/* Community forums — admin only */}
        {isAdmin && <div className="mt-6"><AdminForums /></div>}

        {/* Weekly digest emails — admin only */}
        {isAdmin && <AdminDigest />}

        {/* Cloud library + streaming — admin only */}
        {isAdmin && <AdminLibrary />}

        {/* Activity Log */}
        <section className="mt-10" data-testid="admin-audit-section">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <h2 className="font-display text-xl font-bold flex items-center gap-2">
              <Activity className="w-5 h-5 text-[var(--tt-purple)]" />
              Activity Log
            </h2>
            <span className="text-[10px] uppercase tracking-wider bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] px-2 py-0.5 rounded font-semibold">
              {audit.length}
            </span>
            <select
              value={auditFilter}
              onChange={(e) => setAuditFilter(e.target.value)}
              data-testid="admin-audit-filter"
              className="ml-auto bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-full px-3 py-1.5 text-xs outline-none cursor-pointer hover:border-[var(--tt-primary)]"
            >
              <option value="">All actions</option>
              <option value="torrent.block">Torrent blocks</option>
              <option value="torrent.unblock">Torrent unblocks</option>
              <option value="torrent.classify">Reclassifications</option>
              <option value="torrent.unclassify">Classification resets</option>
              <option value="comment.delete">Comment deletions</option>
              <option value="user.ban">User bans</option>
              <option value="user.unban">User unbans</option>
              <option value="user.promote">User promotions</option>
              <option value="user.demote">User demotions</option>
              <option value="user.promote-mod">Mod promotions</option>
              <option value="user.delete">User deletions</option>
            </select>
          </div>
          <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
            {audit.length === 0 ? (
              <div className="py-10 text-center text-[var(--tt-text-muted)] text-sm">
                No moderator activity yet.
              </div>
            ) : (
              <ul className="divide-y divide-[var(--tt-border)] max-h-96 overflow-y-auto" data-testid="admin-audit-list">
                {audit.map((a) => {
                  const undoable = !a.reverted && [
                    "torrent.block", "torrent.unblock",
                    "torrent.classify", "torrent.unclassify",
                    "comment.delete",
                    "user.ban", "user.unban",
                    "user.promote", "user.demote",
                    "user.promote-mod",
                  ].includes(a.action);
                  const adminOnly = a.action.startsWith("user.");
                  const canUndo = undoable && (isAdmin || !adminOnly);
                  return (
                    <li
                      key={a.audit_id}
                      data-testid={`admin-audit-row-${a.audit_id}`}
                      className={`px-4 py-3 flex items-start gap-3 text-xs ${a.reverted ? "opacity-50" : ""}`}
                    >
                      <div className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{
                        background:
                          a.action.startsWith("torrent.block") || a.action === "user.ban" || a.action === "comment.delete" || a.action === "user.delete" ? "#ef4444" :
                          a.action.includes("classify") ? "var(--tt-purple)" :
                          a.action === "user.promote" || a.action === "user.promote-mod" ? "var(--tt-yellow)" :
                          "#22c55e",
                      }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          <span className="font-mono-tt text-[10px] bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] px-1.5 py-0.5 rounded uppercase">
                            {a.action}
                          </span>
                          <span className="font-semibold">{a.actor_name}</span>
                          <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-[var(--tt-yellow)]/15 text-[var(--tt-yellow)]">
                            {a.actor_role}
                          </span>
                          <span className="text-[10px] text-[var(--tt-text-faint)] font-mono-tt ml-auto">
                            {a.created_at?.slice(0, 19).replace("T", " ")}
                          </span>
                        </div>
                        <div className="mt-1 text-zinc-300 break-words">{a.summary}</div>
                        {a.reverted && (
                          <div className="mt-1 flex items-center gap-1 text-[10px] text-emerald-400 font-mono-tt">
                            <Undo2 className="w-3 h-3" /> Reverted by {a.reverted_by_name} on {a.reverted_at?.slice(0, 19).replace("T", " ")}
                          </div>
                        )}
                      </div>
                      {canUndo ? (
                        <button
                          disabled={busy}
                          onClick={() => action(() => api.post(`/admin/audit/${a.audit_id}/undo`), "Reverted")}
                          data-testid={`admin-audit-undo-${a.audit_id}`}
                          className="flex items-center gap-1 text-[11px] font-semibold px-3 py-1 rounded-full bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] hover:bg-[var(--tt-primary)]/25 shrink-0"
                          title="Undo this action"
                        >
                          <Undo2 className="w-3 h-3" /> Undo
                        </button>
                      ) : a.reverted ? null : (
                        <span className="text-[10px] text-[var(--tt-text-faint)] flex items-center gap-1 shrink-0" title="This action cannot be undone">
                          <AlertCircle className="w-3 h-3" /> {adminOnly && !isAdmin ? "admin-only" : "—"}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </section>

        {/* Cache */}
        {isAdmin && (
        <>
        <AdminSources />
        <AdminAnalytics />
        <AdminPartners />

        <section className="mt-10 mb-12">
          <div className="flex items-center gap-3 mb-4">
            <h2 className="font-display text-xl font-bold">Torrents Cache</h2>
            <button onClick={() => action(() => api.delete("/admin/torrents-cache"), "Cache cleared")}
              disabled={busy || cache.length === 0}
              data-testid="admin-cache-clear"
              className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-full bg-red-500/15 text-red-400 hover:bg-red-500/25 disabled:opacity-40">
              Clear Cache
            </button>
          </div>
          <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-black/40 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">
                  <tr>
                    <th className="text-left px-4 py-3">Title</th>
                    <th className="text-left px-4 py-3">Type</th>
                    <th className="text-left px-4 py-3">TMDB ID</th>
                    <th className="text-left px-4 py-3">Season/Ep</th>
                    <th className="text-left px-4 py-3">Fetched</th>
                  </tr>
                </thead>
                <tbody>
                  {cache.map((c, i) => (
                    <tr key={i} className="border-t border-[var(--tt-border)]">
                      <td className="px-4 py-3">{c.title}</td>
                      <td className="px-4 py-3 text-xs uppercase">{c.media_type}</td>
                      <td className="px-4 py-3 font-mono-tt text-xs">{c.tmdb_id}</td>
                      <td className="px-4 py-3 text-xs">{c.season ? `S${c.season}${c.episode ? `E${c.episode}` : ""}` : "—"}</td>
                      <td className="px-4 py-3 font-mono-tt text-xs text-[var(--tt-text-muted)]">{c.fetched_at?.slice(0, 19).replace("T", " ")}</td>
                    </tr>
                  ))}
                  {cache.length === 0 && (
                    <tr><td colSpan={5} className="py-10 text-center text-[var(--tt-text-muted)]">Cache is empty. Trigger a sync to warm it up.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>
        </>
        )}
      </main>
    </div>
  );
}


const TIER_LABELS = {
  none: { label: "Free", cls: "bg-zinc-700 text-zinc-200" },
  tv: { label: "£5 TV", cls: "bg-cyan-500/20 text-cyan-300" },
  premium: { label: "£10 Premium", cls: "bg-[var(--tt-primary)]/20 text-[var(--tt-primary)]" },
};

function TierControl({ user, busy, onGrant }) {
  const current = user.tier || "none";
  const meta = TIER_LABELS[current] || TIER_LABELS.none;
  const untilStr = user.donor_until
    ? new Date(user.donor_until).toLocaleDateString(undefined, { year: "2-digit", month: "short", day: "numeric" })
    : null;
  return (
    <div className="flex flex-col gap-1 items-start">
      <span
        className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${meta.cls}`}
        data-testid={`admin-user-tier-${user.user_id}`}
      >
        {meta.label}
      </span>
      {untilStr && current !== "none" && (
        <span className="text-[10px] font-mono-tt text-[var(--tt-text-muted)]">until {untilStr}</span>
      )}
      <select
        disabled={busy}
        value={current}
        onChange={(e) => {
          const next = e.target.value;
          if (next === current) return;
          // 12 months grants donor benefit for a year; backend ignores months when tier="none"
          onGrant(next, 12);
        }}
        data-testid={`admin-user-tier-select-${user.user_id}`}
        className="bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded text-[10px] px-1.5 py-0.5 outline-none focus:border-[var(--tt-primary)] disabled:opacity-50"
      >
        <option value="none">Free</option>
        <option value="tv">£5 TV</option>
        <option value="premium">£10 Premium</option>
      </select>
    </div>
  );
}
